import { useCallback, useEffect, useState } from 'react';
import { Send, Trophy, FileEdit, XCircle, type LucideIcon } from 'lucide-react';
import { AnimatedCounter, StaggerChildren, StaggerItem } from '@/components/motion';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { formatoPorcentaje } from '@/lib/utils/formato-precio';
import { tradeService } from '../services/trade-service';
import type { QuoteFunnel as Funnel } from '../types/trade.types';
import type { QuoteOutcome } from '../utils/quote-outcome';

/**
 * El tablero de cotizaciones.
 *
 * El que estaba antes en esta pantalla medía **embarques** —activas, en
 * tránsito, en aduana, entregadas— y aparecía en cero: cuatro números que no
 * hablaban de lo que se estaba mirando.
 *
 * Estos cuatro responden preguntas que alguien se hace de verdad, y **ninguno
 * repite lo que ya dice la tabla de abajo**:
 *
 *   Esperando   → ¿a quién llamo hoy?
 *   En juego    → ¿cuánta plata depende de esas llamadas?
 *   Se ganan    → ¿cómo venimos?
 *   Por vencer  → ¿qué se me escapa esta semana?
 *
 * "En juego" es el que convierte "2 pendientes" en algo que importa. Suma solo
 * las **enviadas**: un borrador no está en juego porque nadie lo vio.
 *
 * Se dibuja solo si hay cotizaciones. Un tablero en cero arriba de una tabla
 * vacía le roba el lugar al mensaje que sí ayuda —"todavía no cotizaste nada"—.
 */

interface Tarjeta {
  icon: LucideIcon;
  label: string;
  value: number;
  /** Formato del número: entero, dinero o porcentaje. */
  kind: 'count' | 'money' | 'percent';
  hint: string;
  /** Dato secundario debajo del número: la plata, la tasa, el motivo. */
  sub?: string;
  /** El color se reserva para lo accionable. Ver abajo. */
  tone?: 'attention' | 'good';
  /** El estado que filtra al hacerle clic. */
  outcome?: QuoteOutcome;
}

export function QuoteFunnel({
  refreshKey,
  onSelect,
  activo,
}: {
  refreshKey?: number;
  /**
   * Qué hacer al tocar una tarjeta. Sin esto las tarjetas son solo números.
   *
   * 🔴 Ese era el reclamo: "las estadísticas no están vinculadas". Cada tarjeta
   * decía un número y no había forma de ver de qué se componía — había que
   * volver a filtrar a mano en la tabla de abajo, adivinando qué filtro
   * correspondía a qué tarjeta.
   */
  onSelect?: (outcome: QuoteOutcome | 'all') => void;
  /** El estado filtrado ahora, para marcar la tarjeta encendida. */
  activo?: QuoteOutcome | 'all';
}) {
  const [data, setData] = useState<Funnel | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setData(await tradeService.quoteFunnel());
    } catch {
      // Es un resumen, no el contenido: si falla, la tabla sigue sirviendo y no
      // vale la pena interrumpir con un error.
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[88px] w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (!data || data.draft + data.sent + data.closed === 0) return null;

  const tarjetas: Tarjeta[] = [
    {
      icon: FileEdit,
      label: 'Borrador',
      value: data.draft,
      kind: 'count',
      hint: data.draft === 1 ? 'sin enviar todavía' : 'sin enviar todavía',
      outcome: 'DRAFT',
    },
    {
      icon: Send,
      label: 'Esperando respuesta',
      value: data.sent,
      kind: 'count',
      // La plata pasa a ser el dato de abajo, no una tarjeta propia.
      sub: data.pipelineValue > 0 ? dinero(data.pipelineValue) : undefined,
      hint: data.expiringSoon > 0 ? `${data.expiringSoon} por vencer` : 'enviadas, sin cerrar',
      // Es el trabajo del día: a quién hay que llamar.
      tone: data.sent > 0 ? 'attention' : undefined,
      outcome: 'SENT',
    },
    {
      icon: Trophy,
      label: 'Ganadas',
      value: data.accepted,
      kind: 'count',
      sub:
        data.winRatePercent !== null
          ? `${formatoPorcentaje(data.winRatePercent, { decimalesMaximos: 1 })} de ${data.closed} cerradas`
          : undefined,
      hint: data.winRatePercent === null ? 'todavía no cerró ninguna' : 'aceptadas por el cliente',
      tone: data.accepted > 0 ? 'good' : undefined,
      outcome: 'ACCEPTED',
    },
    {
      icon: XCircle,
      label: 'Perdidas',
      value: data.lost,
      kind: 'count',
      // El motivo más repetido, que es lo único accionable de una pérdida.
      sub: data.lostByReason[0]?.count ? `casi todas: ${data.lostByReason[0].label}` : undefined,
      hint:
        data.cancelled > 0
          ? `${data.cancelled} cancelada${data.cancelled === 1 ? '' : 's'} aparte`
          : 'no se cerraron',
      // Sin `tone`: va apagada a propósito. Ver el comentario de arriba.
      outcome: 'LOST',
    },
  ];

  return (
    <StaggerChildren className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {tarjetas.map((t) => (
        <StaggerItem key={t.label}>
          <Tarjeta
            {...t}
            activa={activo === t.outcome}
            onSelect={
              onSelect
                ? () => onSelect(activo === t.outcome ? 'all' : (t.outcome as QuoteOutcome))
                : undefined
            }
          />
        </StaggerItem>
      ))}
    </StaggerChildren>
  );
}

/** Dinero corto: lo que entra abajo de un número grande sin competirle. */
function dinero(v: number): string {
  return `USD ${v.toLocaleString('es-BO', { maximumFractionDigits: 0 })}`;
}

/**
 * Una tarjeta.
 *
 * El color se reserva para lo que pide una acción: ámbar cuando hay respuestas
 * que perseguir o precios por vencer, verde para la tasa de éxito. Si todo
 * estuviera pintado, el color dejaría de significar algo y habría que leer las
 * cuatro para saber cuál mirar.
 */
function Tarjeta({
  icon: Icon,
  label,
  value,
  kind,
  hint,
  sub,
  tone,
  activa,
  onSelect,
}: Tarjeta & { activa?: boolean; onSelect?: () => void }) {
  const money = kind === 'money';
  const clickeable = Boolean(onSelect);

  /*
    Botón de verdad cuando filtra, `div` cuando no.

    Un `div` con `onClick` no se alcanza con el teclado ni se anuncia como algo
    que se puede activar. Y como acá el clic CAMBIA lo que muestra la tabla de
    abajo, tiene que ser operable sin mouse.
  */
  const Contenedor = clickeable ? 'button' : 'div';

  return (
    <Contenedor
      {...(clickeable
        ? { type: 'button' as const, onClick: onSelect, 'aria-pressed': activa }
        : {})}
      className={cn(
        'group relative w-full overflow-hidden rounded-xl border bg-card p-3.5 text-left transition-all',
        'hover:-translate-y-0.5 hover:shadow-md',
        clickeable && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2',
        tone === 'attention' && 'border-alerta-borde ',
        tone === 'good' && 'border-ok-borde ',
        // La encendida se marca con el anillo, no con otro color: el color ya
        // está diciendo otra cosa (qué hay que atender).
        activa && 'ring-2 ring-primary ring-offset-1'
      )}
    >
      {/* Un resplandor apenas visible, para que la tarjeta no sea una caja plana. */}
      <div
        className={cn(
          'pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100',
          tone === 'attention' && 'bg-alerta/25',
          tone === 'good' && 'bg-ok/25',
          !tone && 'bg-primary/10'
        )}
        aria-hidden
      />

      <div className="flex items-center gap-1.5">
        <Icon
          className={cn(
            'h-3.5 w-3.5',
            tone === 'attention' && 'text-alerta',
            tone === 'good' && 'text-ok',
            !tone && 'text-muted-foreground'
          )}
          aria-hidden
        />
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
      </div>

      <p
        className={cn(
          'mt-1 font-titulo font-semibold tabular-nums',
          // El dinero necesita más lugar: seis cifras con separadores no entran
          // en el mismo cuerpo que un "2".
          money ? 'text-xl' : 'text-2xl',
          tone === 'attention' && 'text-alerta ',
          tone === 'good' && 'text-ok '
        )}
      >
        <AnimatedCounter
          target={value}
          // Un decimal en el porcentaje: 87,5 % redondeado a 88 % dice algo que
          // no es, y con pocas cotizaciones cerradas la diferencia se nota.
          decimals={kind === 'percent' ? 1 : 0}
          locale
          prefix={money ? 'USD ' : ''}
          suffix={kind === 'percent' ? ' %' : ''}
          duration={0.9}
        />
      </p>

      {/* El dato secundario: la plata, la tasa, el motivo. Debajo del número y
          más chico, para que no le compita. */}
      {sub && <p className="mt-0.5 text-xs font-medium tabular-nums text-foreground/70">{sub}</p>}

      <p className="mt-0.5 text-xs leading-tight text-muted-foreground">{hint}</p>
    </Contenedor>
  );
}
